import { Component, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ApiPaginationResult,
  PaginationResult,
} from '../../../../shared/models/api-result.model';
import { MessageService } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RouterLink } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { Subject, debounceTime, takeUntil } from 'rxjs';

import { ProductList, ProductStatus } from '../../../models/product.model';
import { ProductService } from '../../../services/product.service';
import { SelectOption } from '../../../../shared/models/dropdowm.model';
import { TagStatusComponent } from '../../../../shared/components/tag-status/tag-status.component';
import { PaginationService } from '../../../../shared/services/pagination.service';

@Component({
  selector: 'app-product-list',
  imports: [
    FormsModule,
    InputTextModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    ButtonModule,
    ToolbarModule,
    BreadcrumbModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    RouterLink,
    ToastModule,
    TagStatusComponent,
  ],
  templateUrl: './product-list.component.html',
})
export class ProductListComponent implements OnDestroy {
  @ViewChild('table') table?: Table;

  private productService = inject(ProductService);
  private messageService = inject(MessageService);
  paginationService = inject(PaginationService);

  products = signal<ProductList[]>([]);

  pageNumber = signal<number>(1);
  pageSize = signal<number>(10);
  totalRecords = signal<number>(0);
  totalPages = signal<number>(0);

  loading = signal(false);

  productStatusOptions = this.productService.getProductStatusOptions();

  nameFilter?: string;
  skuFilter?: string;

  private destroyFilter$ = new Subject<void>();
  private filterChanged$ = new Subject<void>();

  constructor() {
    this.filterChanged$
      .pipe(debounceTime(500), takeUntil(this.destroyFilter$))
      .subscribe(() => {
        this.pageNumber.set(0);
        this.table?.reset();
      });
  }

  ngOnDestroy(): void {
    this.destroyFilter$.next();
    this.destroyFilter$.complete();
  }

  onFilterChange(): void {
    this.filterChanged$.next();
  }

  loadData(event: any): void {
    const page = this.paginationService.mapLazyEventToPageRequest(
      event,
      this.pageSize()
    );
    this.pageNumber.set(page.pageNumber);
    this.pageSize.set(page.pageSize);
    
    this.loading.set(true);

    this.productService
      .getAll(
        this.pageNumber(),
        this.pageSize(),
        this.nameFilter,
        this.skuFilter
      )
      .subscribe({
        next: (response: ApiPaginationResult) => {
          this.loading.set(false);

          const page = response.data as PaginationResult;
          this.products.set(page.items as ProductList[]);
          this.pageNumber.set(page.pageNumber);
          this.pageSize.set(page.pageSize);
          this.totalRecords.set(page.totalCount);
          this.totalPages.set(page.totalPages);
        },
        error: (e) => {
          this.loading.set(false);

          if (e instanceof HttpErrorResponse) {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao carregar os produtos',
              life: 3000,
            });
          }
        },
      });
  }

  getStatusDescription(status: ProductStatus): string {
    const option = this.productStatusOptions.filter(
      (x: SelectOption) => x.value == ProductStatus[status]
    );
    return option.length > 0 ? option[0].label : '-';
  }
}
